import { createContext, useContext, useState, type ReactNode } from "react";
import { useAuth } from "@/context/AuthContext";
export interface Customer {
  preferredContactMethod?: string;
  id: string;
  name: string;
  account: string;
  category: string;
  type: string;
  address: string;
  city: string;
  postcode: string;
  phone: string;
  email: string;
  owner: string;
  grid: string;
  discount: string;
  callDay: string;
  deliveryDay: string;
  onStop: boolean;
  notes: string;
}
export interface Action {
  id: string;
  customerId: string;
  type: string;
  text: string;
  due: string;
  owner: string;
  closed: boolean;
}
export interface Entry {
  id: string;
  customerId: string;
  kind: "note" | "contact" | "order";
  text: string;
  date: string;
}
const customers: Customer[] = [
  {
    id: "1",
    name: "The Golden Lion",
    account: "GL1001",
    category: "Trading",
    type: "Public house",
    address: "12 Market Street",
    city: "Skipton",
    postcode: "BD23 1DG",
    phone: "01756 555 012",
    email: "manager@goldenlion.example",
    owner: "Alex Morgan",
    grid: "A1",
    discount: "5%",
    callDay: "Monday",
    deliveryDay: "Wednesday",
    onStop: false,
    notes: "Independent free house. Speak to Sarah about seasonal ales.",
  },
  {
    id: "2",
    name: "Riverside Hotel",
    account: "RH1002",
    category: "Trading",
    type: "Hotel",
    address: "8 Riverside Road",
    city: "Ilkley",
    postcode: "LS29 8HB",
    phone: "01943 555 024",
    email: "orders@riverside.example",
    owner: "Jamie Taylor",
    grid: "B2",
    discount: "10%",
    callDay: "Tuesday",
    deliveryDay: "Thursday",
    onStop: false,
    notes: "Deliveries via rear entrance.",
  },
  {
    id: "3",
    name: "The Old Mill",
    account: "OM1003",
    category: "Prospect",
    type: "Restaurant",
    address: "Mill Lane",
    city: "Keighley",
    postcode: "BD21 5AB",
    phone: "01535 555 036",
    email: "hello@oldmill.example",
    owner: "Alex Morgan",
    grid: "A2",
    discount: "0%",
    callDay: "Wednesday",
    deliveryDay: "Friday",
    onStop: false,
    notes: "Interested in a tasting visit.",
  },
  {
    id: "4",
    name: "The Fox & Hounds",
    account: "FH1004",
    category: "Trading",
    type: "Public house",
    address: "45 High Street",
    city: "Bradford",
    postcode: "BD1 2AA",
    phone: "01274 555 048",
    email: "bar@fox.example",
    owner: "Jamie Taylor",
    grid: "B1",
    discount: "5%",
    callDay: "Thursday",
    deliveryDay: "Friday",
    onStop: true,
    notes: "Credit account on hold.",
  },
];
const actions: Action[] = [
  {
    id: "1",
    customerId: "1",
    type: "Telephone",
    text: "Confirm next week’s order and introduce the autumn range.",
    due: "2026-10-08",
    owner: "Alex Morgan",
    closed: false,
  },
  {
    id: "2",
    customerId: "3",
    type: "Visit",
    text: "Arrange a tasting with the new restaurant manager.",
    due: "2026-10-07",
    owner: "Alex Morgan",
    closed: false,
  },
  {
    id: "3",
    customerId: "2",
    type: "Email",
    text: "Send updated pricing and delivery schedule.",
    due: "2026-10-09",
    owner: "Jamie Taylor",
    closed: false,
  },
];
function restore<T>(key: string, fallback: T): T {
  try {
    return JSON.parse(sessionStorage.getItem(key) || "null") ?? fallback;
  } catch {
    return fallback;
  }
}
function useStored<T>(key: string, fallback: T) {
  const [value, setValue] = useState<T>(() => restore(key, fallback));
  return [
    value,
    (next: T) => {
      sessionStorage.setItem(key, JSON.stringify(next));
      setValue(next);
    },
  ] as const;
}
function useStore() {
  const { session } = useAuth();
  const suffix =
    session?.businessId === "demo" ? "" : `:${session?.businessId ?? "none"}`;
  const [customers, setCustomers] = useStored(
    `demo-customers${suffix}`,
    customersSeed,
  );
  const [actions, setActions] = useStored(`demo-actions${suffix}`, actionsSeed);
  const [entries, setEntries] = useStored<Entry[]>(`demo-entries${suffix}`, []);
  return { customers, setCustomers, actions, setActions, entries, setEntries };
}
const customersSeed = customers,
  actionsSeed = actions;
const Context = createContext<ReturnType<typeof useStore> | null>(null);
export function DemoProvider({ children }: { children: ReactNode }) {
  const store = useStore();
  return <Context.Provider value={store}>{children}</Context.Provider>;
}
export function useDemo() {
  const store = useContext(Context);
  if (!store) throw Error("Missing demo provider");
  return store;
}
